import React, { useState, useEffect } from "react";
import { useUser } from "@clerk/clerk-react";
import SchoolDropdown from "../school-dropdown";
import Button from "../button";
import apiClient from "../../services/api-client";
import { useClerkAuth } from "../../hooks/use-clerk-auth";
import s from "./styles.module.scss";

const SchoolProfilePage = () => {
  const { user } = useUser();
  const { getSessionToken } = useClerkAuth();
  const [school, setSchool] = useState(null);
  const [originalSchool, setOriginalSchool] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Load current school from Clerk metadata
  useEffect(() => {
    if (user?.unsafeMetadata) {
      const metadata = user.unsafeMetadata;
      if (metadata.school_id || metadata.school_name) {
        const schoolData = {
          school_id: metadata.school_id || null,
          school_name: metadata.school_name || null,
          custom_school: metadata.custom_school || null,
        };
        setSchool(schoolData);
        setOriginalSchool(schoolData);
      }
    }
  }, [user]);

  // Check if school has changed from original
  const hasChanges = () => {
    if (!school && !originalSchool) return false;
    if (!school || !originalSchool) return true;

    // Normalize null/undefined for comparison
    const schoolId = school.school_id ?? null;
    const originalId = originalSchool.school_id ?? null;
    const schoolName = school.school_name ?? null;
    const originalName = originalSchool.school_name ?? null;
    const customSchool = school.custom_school ?? null;
    const originalCustom = originalSchool.custom_school ?? null;

    return (
      schoolId !== originalId ||
      schoolName !== originalName ||
      customSchool !== originalCustom
    );
  };

  const handleSave = async () => {
    if (!school?.school_name) {
      setMessage({ type: "error", text: "Please select a school" });
      return;
    }

    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      // Update Clerk metadata
      await user.update({
        unsafeMetadata: {
          ...user.unsafeMetadata,
          school_id: school.school_id,
          school_name: school.school_name,
          custom_school: school.custom_school || null,
        },
      });

      // Sync to backend
      try {
        await apiClient.post(
          "/user/school",
          {
            school_id: school.school_id || null,
            custom_school: school.custom_school || null
          },
          getSessionToken
        );
      } catch (backendError) {
        console.error("Backend sync error:", backendError);
      }

      setMessage({ type: "success", text: "School updated successfully!" });
      setOriginalSchool(school);
    } catch (err) {
      console.error("Error updating school:", err);
      setMessage({ type: "error", text: "Failed to update school. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const displaySchool = school?.custom_school
    ? `Other: ${school.custom_school}`
    : school?.school_name || "Not set";

  return (
    <div className={s.schoolPage}>
      <h2 className={s.title}>School</h2>
      <p className={s.description}>
        Your current school: <strong>{displaySchool}</strong>
      </p>

      <div className={s.dropdownWrapper}>
        <SchoolDropdown
          value={school}
          onChange={setSchool}
          placeholder="Search for your school..."
        />
      </div>

      {message.text && (
        <div className={`${s.message} ${s[message.type]}`}>
          {message.text}
        </div>
      )}

      <Button
        variant="primary"
        onClick={handleSave}
        disabled={saving || !hasChanges()}
      >
        {saving ? "Saving..." : "Save"}
      </Button>
    </div>
  );
};

export default SchoolProfilePage;
